import { useContext, createContext, useState, useEffect } from "react";
import { Languages, TranslationObject } from "../types";
import { getStoredLanguageRaw, setStoredLanguage } from "../utils/language";

import ENGLISH_TRANSLATIONS from "../translations/english";
import DEUTSCH_TRANSLATIONS from "../translations/deutsch";

type TranslationsContextValues = {
  translation: TranslationObject;
  language: Languages;
  setLanguage(language: Languages): void;
};

const defaultValues: TranslationsContextValues = {
  translation: DEUTSCH_TRANSLATIONS,
  language: Languages.DEUTSCH,
  setLanguage: (language: Languages) => {},
};

export const TranslationsContext =
  createContext<TranslationsContextValues>(defaultValues);

export function useTranslationsContext() {
  return useContext(TranslationsContext);
}

type TranslationsProviderProps = {
  children: React.ReactNode;
};

export function TranslationsProvider({
  children,
}: TranslationsProviderProps): JSX.Element {
  const [language, setLanguageState] = useState<Languages>(() => {
    const stored = getStoredLanguageRaw();
    return stored === "ENGLISH" ? Languages.ENGLISH : Languages.DEUTSCH;
  });
  const [translation, setTranslation] =
    useState<TranslationObject>(
      language === Languages.ENGLISH ? ENGLISH_TRANSLATIONS : DEUTSCH_TRANSLATIONS,
    );

  function setLanguage(lang: Languages) {
    setStoredLanguage(lang);
    setLanguageState(lang);
  }

  useEffect(() => {
    if (language === Languages.ENGLISH) {
      setTranslation(ENGLISH_TRANSLATIONS);
    } else {
      setTranslation(DEUTSCH_TRANSLATIONS);
    }
  }, [language]);

  const value = {
    language,
    setLanguage,
    translation,
  };

  return (
    <TranslationsContext.Provider value={value}>
      {children}
    </TranslationsContext.Provider>
  );
}
